import {
   array,
   boolean,
   description,
   isoTimestamp,
   nullable,
   number,
   object,
   optional,
   picklist,
   pipe,
   string,
   type InferOutput,
} from 'valibot'

/**
 * Valibot schemas for `GET /v1/habits/changes`, the delta read for habits and
 * their completions. Writes keep using the per-habit `/v1/habits` routes.
 */

const isoDateTimeSchema = pipe(string(), isoTimestamp())

export const habitPausePeriodSchema = object({
   start: isoDateTimeSchema,
   end: pipe(
      nullable(isoDateTimeSchema),
      description('Null while the pause is ongoing.'),
   ),
})

export const habitChangeSchema = object({
   id: string(),
   name: optional(string()),
   kind: optional(picklist(['recurring', 'one-off', 'calendar'])),
   scheduleDays: optional(number()),
   dueDate: optional(nullable(isoDateTimeSchema)),
   scheduleRule: optional(nullable(string())),
   paused: optional(boolean()),
   lastCompletedAt: optional(nullable(isoDateTimeSchema)),
   alsoCompletes: optional(array(string())),
   tags: optional(array(string())),
   pausePeriods: optional(array(habitPausePeriodSchema)),
   createdAt: optional(isoDateTimeSchema),
   updatedAt: pipe(
      isoDateTimeSchema,
      description('Last edit time; drives last-writer-wins for habit fields.'),
   ),
   deletedAt: pipe(
      nullable(isoDateTimeSchema),
      description(
         'Set when the habit is a tombstone; then only `id` and times are present.',
      ),
   ),
})

export const habitCompletionChangeSchema = object({
   habitId: string(),
   completedAt: isoDateTimeSchema,
   deletedAt: pipe(
      nullable(isoDateTimeSchema),
      description('Set when the completion was undone or its habit deleted.'),
   ),
   updatedAt: isoDateTimeSchema,
})

export const habitChangesResponseSchema = object({
   ok: boolean(),
   habits: array(habitChangeSchema),
   completions: array(habitCompletionChangeSchema),
   serverTime: pipe(
      isoDateTimeSchema,
      description('Pass back as `since` on the next delta request.'),
   ),
})

export type HabitChange = InferOutput<typeof habitChangeSchema>
export type HabitCompletionChange = InferOutput<typeof habitCompletionChangeSchema>
export type HabitChangesResponse = InferOutput<typeof habitChangesResponseSchema>
